import React, { createContext, useContext, useState, useCallback, useMemo, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Snackbar, Alert } from '@mui/material';

type SnackbarSeverity = 'success' | 'error' | 'warning' | 'info';

interface SnackbarOptions {
  action?: React.ReactNode;
  /** Своя иконка вместо стандартной (галочка/крестик), напр. иконка выигранного предмета */
  icon?: React.ReactNode;
  /**
   * Лимит для группы: уведомлений с одним `key` на экране не больше `max`, при новом убирается
   * самое старое из этой группы. Остальные уведомления не трогает. Без `group` — без лимита.
   */
  group?: { key: string; max: number };
}

interface SnackbarItem {
  id: number;
  message: string;
  severity: SnackbarSeverity;
  action?: React.ReactNode;
  icon?: React.ReactNode;
  groupKey?: string;
}

interface SnackbarContextType {
  showSuccess: (message: string, options?: SnackbarOptions) => void;
  showError: (message: string, options?: SnackbarOptions) => void;
  showWarning: (message: string, options?: SnackbarOptions) => void;
  showInfo: (message: string, options?: SnackbarOptions) => void;
}

const SnackbarContext = createContext<SnackbarContextType | undefined>(undefined);

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [queue, setQueue] = useState<SnackbarItem[]>([]);
  const nextId = useRef(0);

  const showSnackbar = useCallback((message: string, severity: SnackbarSeverity, options?: SnackbarOptions) => {
    const item: SnackbarItem = { id: nextId.current++, message, severity, action: options?.action, icon: options?.icon, groupKey: options?.group?.key };
    setQueue(prev => {
      const group = options?.group;
      if (!group) return [...prev, item];
      // Сколько старых из группы надо убрать, чтобы вместе с новым осталось не больше max
      let toDrop = prev.filter(i => i.groupKey === group.key).length - (group.max - 1);
      const kept = prev.filter(i => !(i.groupKey === group.key && toDrop-- > 0));
      return [...kept, item];
    });
  }, []);

  const showSuccess = useCallback((message: string, options?: SnackbarOptions) => showSnackbar(message, 'success', options), [showSnackbar]);
  const showError = useCallback((message: string, options?: SnackbarOptions) => showSnackbar(message, 'error', options), [showSnackbar]);
  const showWarning = useCallback((message: string, options?: SnackbarOptions) => showSnackbar(message, 'warning', options), [showSnackbar]);
  const showInfo = useCallback((message: string, options?: SnackbarOptions) => showSnackbar(message, 'info', options), [showSnackbar]);

  const handleClose = useCallback((id: number) => {
    setQueue(prev => prev.filter(item => item.id !== id));
  }, []);

  return (
    <SnackbarContext.Provider value={useMemo(() => ({ showSuccess, showError, showWarning, showInfo }), [showSuccess, showError, showWarning, showInfo])}>
      {children}
      {ReactDOM.createPortal(
        <>
          {queue.map((item, index) => (
            <Snackbar
              key={item.id}
              open={true}
              autoHideDuration={6000}
              onClose={(_, reason) => {
                if (reason === 'clickaway') return;
                handleClose(item.id);
              }}
              anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
              sx={{
                zIndex: 99999,
                top: `${24 + index * 60}px !important`,
              }}
            >
              <Alert
                onClose={() => handleClose(item.id)}
                severity={item.severity}
                action={item.action}
                icon={item.icon}
                sx={{
                  maxWidth: { xs: 'calc(100vw - 32px)', sm: '400px' },
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  backdropFilter: 'blur(10px)',
                  alignItems: 'center',
                  // Своя иконка (предмет) — без приглушения и лишних отступов стандартного значка
                  ...(item.icon && { '& .MuiAlert-icon': { opacity: 1, py: 0.5 } }),
                }}
              >
                {item.message}
              </Alert>
            </Snackbar>
          ))}
        </>,
        document.body
      )}
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = (): SnackbarContextType => {
  const context = useContext(SnackbarContext);
  if (!context) {
    throw new Error('useSnackbar must be used within a SnackbarProvider');
  }
  return context;
};
